import { useState } from "react";
import api, { API } from "../lib/api";
import { FRAME_COLORS } from "../context/OrderContext";
import { Camera, Check, Loader2, X, Copy, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import ScreenVisualizer from "./ScreenVisualizer";

function PhotoUpload({ index, item, onUploaded, onCleared }) {
  const [uploading, setUploading] = useState(false);

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded({ photo_path: res.data.path, photo_url: `${API}/files/${res.data.path}` });
      toast.success("Photo uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (item.photo_url) {
    return (
      <div className="relative">
        <img
          data-testid={`item-${index}-photo-preview`}
          src={item.photo_url}
          alt="Uploaded"
          className="w-full h-32 object-cover rounded-xl border border-[var(--brand-border)]"
        />
        <button
          type="button"
          data-testid={`item-${index}-photo-remove`}
          onClick={onCleared}
          className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <label
      data-testid={`item-${index}-photo-upload`}
      className="block cursor-pointer border-2 border-dashed border-[var(--brand-border)] rounded-xl py-6 text-center hover:border-[var(--brand-green)] transition-colors"
    >
      <input type="file" accept="image/*" className="hidden" onChange={onChange}
        data-testid={`item-${index}-photo-input`} />
      {uploading ? (
        <Loader2 className="w-6 h-6 mx-auto animate-spin brand-text" />
      ) : (
        <>
          <Camera className="w-6 h-6 mx-auto brand-text" />
          <p className="text-sm font-medium mt-1.5 brand-text">Add a photo (optional)</p>
        </>
      )}
    </label>
  );
}

/** Inch fraction options for the dropdown next to whole-inch input.
 * 1/16" precision matches the screen-fabrication industry standard. */
const INCH_FRACTIONS = [
  { value: 0, label: "0" },
  { value: 1, label: "1/16" },
  { value: 2, label: "1/8" },
  { value: 3, label: "3/16" },
  { value: 4, label: "1/4" },
  { value: 5, label: "5/16" },
  { value: 6, label: "3/8" },
  { value: 7, label: "7/16" },
  { value: 8, label: "1/2" },
  { value: 9, label: "9/16" },
  { value: 10, label: "5/8" },
  { value: 11, label: "11/16" },
  { value: 12, label: "3/4" },
  { value: 13, label: "13/16" },
  { value: 14, label: "7/8" },
  { value: 15, label: "15/16" },
];

/** Split a stored decimal value back into whole inches + nearest 1/16th. */
function splitInches(value) {
  if (value === "" || value === null || value === undefined) return { whole: "", sixteenths: 0 };
  const n = Number(value);
  if (Number.isNaN(n) || n < 0) return { whole: "", sixteenths: 0 };
  const total16 = Math.round(n * 16);
  return { whole: Math.floor(total16 / 16), sixteenths: total16 % 16 };
}

/** Combine whole inches + sixteenths back into a decimal. */
function combineInches(whole, sixteenths) {
  if (whole === "" || whole === null) return sixteenths > 0 ? sixteenths / 16 : "";
  const w = Number(whole);
  if (Number.isNaN(w)) return "";
  return w + sixteenths / 16;
}

function DimensionInput({ label, value, onChange, placeholder, testid }) {
  const { whole, sixteenths } = splitInches(value);
  const handleWhole = (v) => {
    if (v === "") {
      onChange(sixteenths > 0 ? sixteenths / 16 : "");
      return;
    }
    onChange(combineInches(v, sixteenths));
  };
  const handleFraction = (v) => {
    const sx = Number(v);
    onChange(combineInches(whole, sx));
  };
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider muted-text">
        {label} <span className="text-[var(--brand-text)]">(in)</span>
      </label>
      <div className="flex gap-2 mt-1 items-stretch">
        <input
          type="number" min="1" step="1"
          inputMode="numeric"
          data-testid={testid}
          className="rounded-input"
          style={{ flex: "1 1 auto", minWidth: 0, width: "auto" }}
          value={whole}
          onChange={(e) => handleWhole(e.target.value)}
          placeholder={placeholder}
        />
        <select
          data-testid={`${testid}-fraction`}
          className="rounded-input text-base font-semibold text-center"
          style={{ width: 120, flex: "0 0 120px", paddingLeft: 8, paddingRight: 8 }}
          value={sixteenths}
          onChange={(e) => handleFraction(e.target.value)}
          aria-label={`${label} fraction (1/16ths of an inch)`}
        >
          {INCH_FRACTIONS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

/** Spring/pull-tab axis picker. Pull tabs go on the OPPOSITE side of the springs,
 * so a single answer covers both. We only need to know which dimension carries them. */
/** 4-option picker: which single edge has the tension springs? Pull tabs go on the OPPOSITE edge. */
function SpringSidePicker({
  value, onChange, idx,
  hasSprings = true, hasPullTabs = true,
  onToggleSprings, onTogglePullTabs,
}) {
  const Card = ({ id, icon: Icon, title, testid }) => {
    const active = value === id;
    return (
      <button
        type="button"
        data-testid={testid}
        onClick={() => onChange(id)}
        className={`option-card text-left transition-colors ${active ? "selected" : ""}`}
        style={{ minHeight: 64, padding: "10px 12px" }}
      >
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 brand-text shrink-0" />
          <p className="font-semibold text-[var(--brand-text)] text-sm flex-1">{title}</p>
          {active && <Check className="w-4 h-4 brand-text" />}
        </div>
      </button>
    );
  };

  return (
    <div className="mb-3">
      <label className="text-xs font-semibold uppercase tracking-wider muted-text">
        Which edge has the tension springs?
      </label>
      <p className="text-[11px] muted-text mt-1 mb-2">
        The pull tabs are on the opposite edge. Look at your existing screen — the springs are on
        the side you press to pop the screen out of the track.
      </p>
      <div className="grid grid-cols-2 gap-2">
        <Card id="top"    icon={ArrowUp}    title="Top edge"    testid={`item-${idx}-spring-top`} />
        <Card id="bottom" icon={ArrowDown}  title="Bottom edge" testid={`item-${idx}-spring-bottom`} />
        <Card id="left"   icon={ArrowLeft}  title="Left edge"   testid={`item-${idx}-spring-left`} />
        <Card id="right"  icon={ArrowRight} title="Right edge"  testid={`item-${idx}-spring-right`} />
      </div>
      <button
        type="button"
        data-testid={`item-${idx}-spring-unsure`}
        onClick={() => onChange("unsure")}
        className={`mt-2 inline-flex items-center gap-1 text-xs font-semibold underline-offset-2 hover:underline ${
          value === "unsure" ? "brand-text" : "muted-text"
        }`}
      >
        <HelpCircle className="w-3.5 h-3.5" />
        I&apos;m not sure — build to standard
      </button>

      <div className="mt-3 flex flex-col gap-2 text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`item-${idx}-has-springs`}
            checked={hasSprings}
            onChange={(e) => onToggleSprings?.(e.target.checked)}
          />
          <span>This screen has tension springs</span>
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            data-testid={`item-${idx}-has-pull-tabs`}
            checked={hasPullTabs}
            onChange={(e) => onTogglePullTabs?.(e.target.checked)}
          />
          <span>This screen has pull tabs</span>
        </label>
      </div>
    </div>
  );
}

/**
 * Reusable measurement form. Used in both the post-checkout in-app step
 * and the public /m/:orderId page (via emailed/SMS'd token link).
 *
 * Props:
 *   items       — array of items state (length === quantity)
 *   onChange    — (nextItems) => void
 *   petscreenAll — whether petscreen is locked-in for every item (decided pre-checkout)
 *   showSpringAxis — show the springs/pull-tabs side picker (windows only). Defaults to true.
 */
export default function MeasurementForm({
  items, onChange, petscreenAll = false, showSpringAxis = true,
}) {
  const updateItem = (idx, patch) => {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const copyToNext = (idx) => {
    if (idx >= items.length - 1) return;
    const src = items[idx];
    onChange(
      items.map((it, i) =>
        i === idx + 1
          ? {
              ...it,
              width: src.width,
              height: src.height,
              frame_color: src.frame_color,
              notes: src.notes,
              spring_side: src.spring_side,
              has_springs: src.has_springs,
              has_pull_tabs: src.has_pull_tabs,
              handle_side: src.handle_side,
              petscreen: petscreenAll ? true : src.petscreen,
            }
          : it,
      ),
    );
    toast.success(`Copied to Item ${idx + 2}`);
  };

  return (
    <div className="space-y-4">
      {items.map((it, idx) => (
        <div key={idx} className="option-card" data-testid={`item-${idx}-card`}>
          <div className="flex items-center justify-between mb-3">
            <p className="font-semibold text-[var(--brand-text)]">Item {idx + 1}</p>
            {Number(it.width) > 0 && Number(it.height) > 0 && (
              <Check className="w-5 h-5 brand-text" />
            )}
          </div>

          {showSpringAxis && (
            <ScreenVisualizer
              width={it.width}
              height={it.height}
              springSide={it.spring_side || ""}
              hasSprings={it.has_springs !== false}
              hasPullTabs={it.has_pull_tabs !== false}
            />
          )}

          <div className="grid grid-cols-1 gap-3 mb-3">
            <DimensionInput
              label="Width"
              value={it.width}
              onChange={(v) => updateItem(idx, { width: v })}
              placeholder="36"
              testid={`item-${idx}-width`}
            />
            <DimensionInput
              label="Height"
              value={it.height}
              onChange={(v) => updateItem(idx, { height: v })}
              placeholder="48"
              testid={`item-${idx}-height`}
            />
          </div>

          {showSpringAxis && (
            <SpringSidePicker
              idx={idx}
              value={it.spring_side || ""}
              onChange={(v) => updateItem(idx, { spring_side: v })}
              hasSprings={it.has_springs !== false}
              hasPullTabs={it.has_pull_tabs !== false}
              onToggleSprings={(v) => updateItem(idx, { has_springs: v })}
              onTogglePullTabs={(v) => updateItem(idx, { has_pull_tabs: v })}
            />
          )}

          {/* Sliding door only: handle/lock side from outside POV. We use
              !showSpringAxis as the door-vs-window signal because the funnel
              already sets it correctly upstream. */}
          {!showSpringAxis && (
            <div className="mb-3">
              <label className="text-xs font-semibold uppercase tracking-wider muted-text">
                Handle / lock side <span className="font-normal normal-case muted-text">(standing outside, facing the door)</span>
              </label>
              <div className="flex gap-2 mt-1">
                {[
                  { v: "left", label: "Left" },
                  { v: "right", label: "Right" },
                ].map((opt) => {
                  const active = it.handle_side === opt.v;
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      data-testid={`item-${idx}-handle-${opt.v}`}
                      onClick={() => updateItem(idx, { handle_side: opt.v })}
                      className={`flex-1 px-4 py-2 rounded-full border-2 text-sm font-semibold ${
                        active
                          ? "brand-bg text-white border-transparent"
                          : "bg-white border-[var(--brand-border)] text-[var(--brand-text)] hover:border-[var(--brand-green)]"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="mb-3">
            <label className="text-xs font-semibold uppercase tracking-wider muted-text">Frame color</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {FRAME_COLORS.map((c) => {
                const active = it.frame_color === c.name;
                return (
                  <button
                    key={c.name} type="button"
                    data-testid={`item-${idx}-color-${c.name.toLowerCase()}`}
                    onClick={() => updateItem(idx, { frame_color: c.name })}
                    className={`px-3 py-1.5 rounded-full border-2 text-sm font-medium ${
                      active
                        ? "brand-bg text-white border-transparent"
                        : "border-[var(--brand-border)] text-[var(--brand-text)]"
                    }`}
                  >
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mb-3">
            <label className="text-xs font-semibold uppercase tracking-wider muted-text">Notes (optional)</label>
            <textarea
              data-testid={`item-${idx}-notes`}
              className="rounded-input mt-1 min-h-[60px]"
              value={it.notes || ""}
              onChange={(e) => updateItem(idx, { notes: e.target.value })}
              placeholder="Anything we should know?"
            />
          </div>

          {petscreenAll && (
            <div className="text-xs brand-text font-semibold mb-3" data-testid={`item-${idx}-petscreen-locked`}>
              PetScreen included on this item (+$25)
            </div>
          )}

          <PhotoUpload
            index={idx}
            item={it}
            onUploaded={(p) => updateItem(idx, p)}
            onCleared={() => updateItem(idx, { photo_path: null, photo_url: null })}
          />

          {idx < items.length - 1 && (
            <button
              type="button"
              data-testid={`item-${idx}-copy-next`}
              onClick={() => copyToNext(idx)}
              className="mt-3 w-full inline-flex items-center justify-center gap-2 py-2 rounded-full border-2 border-dashed border-[var(--brand-border)] text-sm font-semibold brand-text hover:border-[var(--brand-green)] transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy measurements to Item {idx + 2}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
